import { useState, useCallback, useEffect, useMemo } from 'react';
import { TemplateScenesService } from './templateScenesService';
import type { GalleryTemplate } from './types';
import { useAccountChanges } from '@/modules/mcpStream';
import { createReadSequence } from '@/utils/readSequence';

export const useTemplateScenes = () => {
  const [templateScenes, setTemplateScenes] = useState<GalleryTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reads] = useState(createReadSequence);

  const templateScenesService = useMemo(() => new TemplateScenesService(), []);

  // A background read that fails keeps the list shown; a first or asked-for read shows the error
  const read = useCallback(({ background }: { background: boolean }) => {
    const seq = reads.next();
    return templateScenesService.fetchTemplateScenes()
      .then(scenes => {
        if (!reads.isLatest(seq)) return;
        setTemplateScenes(scenes);
        setError(null);
      })
      .catch(err => {
        if (!reads.isLatest(seq)) return;
        console.error('Error fetching template scenes:', err);
        if (background) return;
        setError("Couldn't load the templates.");
      })
      .finally(() => {
        if (reads.isLatest(seq)) setLoading(false);
      });
  }, [templateScenesService, reads]);

  useEffect(() => {
    void read({ background: false });
  }, [read]);

  // A submission accepted or a template's story changed elsewhere: the list reads again in place
  useAccountChanges(['templateSubmission', 'story'], () => void read({ background: true }), 1000);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    void read({ background: false });
  }, [read]);

  return {
    templateScenes,
    loading,
    error,
    refresh
  };
};
